import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { auditService } from './auditService';
import type {
  ApiResponse,
  BvOrganisation,
  BvOrganisationFormData,
  BvContact,
  BvContactFormData,
} from '../types/billValidationTypes';

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  return data?.organisation_id ?? null;
}

export const organisationService = {
  /** Bill Validation clients. Deactivated ones (job 28) are excluded unless asked
   *  for — the Clients list needs them to offer re-activation, but the new-site
   *  picker and the exports must not offer a client that was taken out of BV. */
  list: async (opts?: { includeInactive?: boolean }): Promise<ApiResponse<BvOrganisation[]>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      // Paginate clients to handle >1000 rows (Supabase default cap)
      const allClients: BvOrganisation[] = [];
      const PAGE_SIZE = 1000;
      let page = 0;
      while (true) {
        let query = supabase
          .from('client')
          .select('id, org_id, name, abn, sf_account_id, status, bill_val, extn, broker, shield_platinum, drive_link, site_count, nmis, created_at')
          .eq('org_id', orgId);
        if (!opts?.includeInactive) query = query.eq('bill_val', 'active');

        const { data, error } = await query
          .order('name')
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        allClients.push(...(data ?? []));
        if (!data?.length || data.length < PAGE_SIZE) break;
        page++;
      }
      const data = allClients;

      // `client.site_count` is maintained by trg_client_site_rollup (migration
      // 055) and is authoritative — use it as selected.
      //
      // It used to be recomputed here by paging every `site` row and counting
      // per client, but that paging had no ORDER BY. Range paging over an
      // unordered result set has no stable row order, so pages overlapped and
      // skipped: counts came out wrong for a different handful of clients on
      // every call, while the total row count still matched and hid it.
      return { success: true, data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  getById: async (id: string): Promise<ApiResponse<BvOrganisation>> => {
    try {
      const { data, error } = await supabase
        .from('client')
        .select('id, org_id, name, abn, sf_account_id, status, bill_val, extn, broker, shield_platinum, drive_link, site_count, created_at')
        .eq('id', id)
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (form: BvOrganisationFormData): Promise<ApiResponse<BvOrganisation>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const { parent_org_id, google_drive_link, broker_id, shield_platinum: _ignored, ...rest } = form;
      // Shield Platinum is not asked for: a client created in Bill Validation is
      // a Shield Platinum client by definition (job 10), the same rule sites got
      // in migration 068. Written to the real column — the flag's single source
      // of truth — and mirrored into extn so a BV build running against a
      // database without migration 069 still reads it.
      const extn = { bv: { parent_org_id, google_drive_link, broker_id, shield_platinum: true } };

      // `client.bill_val` defaults to 'inactive' (migration 037) and the BV
      // clients list filters on bill_val = 'active'. A client created from the
      // BV module is by definition a BV client, so opt it in explicitly —
      // otherwise it saves fine but never appears in the list or search.
      const { data, error } = await supabase
        .from('client')
        .insert({ ...rest, shield_platinum: true, org_id: orgId, extn, bill_val: 'active' })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  update: async (id: string, form: Partial<BvOrganisationFormData>): Promise<ApiResponse<BvOrganisation>> => {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('client')
        .select('extn, name, bill_val, shield_platinum')
        .eq('id', id)
        .single();
      if (fetchErr) throw fetchErr;

      const { parent_org_id, google_drive_link, broker_id, shield_platinum, ...rest } = form;
      // Shield Platinum only ever promotes, matching the site rule in migration
      // 068: every Bill Validation client is Shield Platinum, so an edit that
      // arrives without the flag — the form no longer offers it — must not strip
      // it, and the real column is written alongside extn rather than instead of
      // it, because Tenders and the exports read the column.
      const promoteShield = shield_platinum === true;
      const bvExtn = {
        ...(existing?.extn?.bv ?? {}),
        ...(parent_org_id !== undefined ? { parent_org_id } : {}),
        ...(google_drive_link !== undefined ? { google_drive_link } : {}),
        ...(broker_id !== undefined ? { broker_id } : {}),
        ...(promoteShield ? { shield_platinum: true } : {}),
      };
      const extn = { ...(existing?.extn ?? {}), bv: bvExtn };

      // No updated_at on client table — Supabase triggers handle it if configured
      const { data, error } = await supabase
        .from('client')
        .update({ ...rest, ...(promoteShield ? { shield_platinum: true } : {}), extn })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      // Audit (job 15). A client edit isn't tied to one site, so no site_id —
      // listForSite pulls it into a site's History via the site's client_id.
      // Fire-and-forget: a failed audit write never fails the user's edit.
      const flatten = (extnBv: Record<string, unknown>, cols: Record<string, unknown>) => ({
        name:              cols.name ?? null,
        bill_val:          cols.bill_val ?? null,
        shield_platinum:   cols.shield_platinum ?? null,
        parent_org_id:     extnBv.parent_org_id ?? null,
        google_drive_link: extnBv.google_drive_link ?? null,
        broker_id:         extnBv.broker_id ?? null,
      });
      const beforeRow = (existing ?? {}) as Record<string, unknown>;
      const afterRow = data as Record<string, unknown>;
      void auditService.logFieldChanges(
        { entity_type: 'organisation', entity_id: id },
        flatten((beforeRow.extn as { bv?: Record<string, unknown> })?.bv ?? {}, beforeRow),
        flatten(bvExtn as Record<string, unknown>, afterRow),
      );

      return { success: true, data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Rows that reference a client and would be affected by removing it. Used to
   *  explain a refusal rather than letting a foreign-key error surface raw. */
  countDependents: async (id: string): Promise<ApiResponse<{ sites: number; contacts: number; bills: number }>> => {
    try {
      const counted = async (table: string, column: string, value: string) => {
        const { count, error } = await supabase
          .from(table)
          .select('id', { count: 'exact', head: true })
          .eq(column, value);
        if (error) throw error;
        return count ?? 0;
      };

      const { data: siteRows, error: siteErr } = await supabase
        .from('site')
        .select('id')
        .eq('client_id', id);
      if (siteErr) throw siteErr;
      const siteIds = (siteRows ?? []).map((s) => (s as { id: string }).id);

      // Bills hang off sites, not the client, so they only surface by walking the
      // site list — and they are the reason a delete gets refused.
      let bills = 0;
      if (siteIds.length) {
        const { count, error } = await supabase
          .from('bv_bill')
          .select('id', { count: 'exact', head: true })
          .in('site_id', siteIds);
        if (error) throw error;
        bills = count ?? 0;
      }

      const contacts = await counted('contact', 'client_id', id);
      return { success: true, data: { sites: siteIds.length, contacts, bills } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Take a client out of Bill Validation without destroying anything.
   *
   *  This is the reversible half of job 28 and the right answer for a client that
   *  is simply no longer being validated: `bill_val` is what the BV lists filter
   *  on, so flipping it to 'inactive' hides the client and its history stays
   *  intact — including for the Tenders module, which shares this table and must
   *  not be affected. */
  setBillValidationActive: async (id: string, active: boolean): Promise<ApiResponse<BvOrganisation>> => {
    try {
      const { data, error } = await supabase
        .from('client')
        .update({ bill_val: active ? 'active' : 'inactive' })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      void auditService.log({
        entity_type: 'organisation',
        entity_id:   id,
        action:      'status_change',
        field:       'bill_val',
        old_value:   active ? 'inactive' : 'active',
        new_value:   active ? 'active' : 'inactive',
        note:        active
          ? 'Client re-activated for Bill Validation'
          : 'Client deactivated for Bill Validation — data retained',
      });

      return { success: true, data: data as BvOrganisation };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Delete a client outright — for the test entities job 28 asks about.
   *
   *  Refuses while any of its sites carry bills, because those are real
   *  validation history: `site.client_id` and `bv_bill.site_id` both CASCADE, so
   *  the database would take the whole tree down without complaint. Deactivating
   *  is the answer for a client with history; deleting is for one created by
   *  mistake. */
  delete: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const deps = await organisationService.countDependents(id);
      if (!deps.success || !deps.data) {
        return { success: false, error: deps.error ?? 'Dependency check failed' };
      }
      if (deps.data.bills > 0) {
        return {
          success: false,
          error: `This client has ${deps.data.bills} bill${deps.data.bills === 1 ? '' : 's'} `
            + `across ${deps.data.sites} site${deps.data.sites === 1 ? '' : 's'}. `
            + 'Delete those bills first, or deactivate the client instead of deleting it.',
        };
      }

      // `.select()` so an RLS no-op is distinguishable from a real delete:
      // without a DELETE policy Postgres removes nothing and still reports success.
      const { data, error } = await supabase.from('client').delete().eq('id', id).select('id');
      if (error) throw error;
      if (!data?.length) {
        return { success: false, error: 'Client was not deleted — you may not have permission to delete clients.' };
      }
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // ---- Contacts ----

  getContactById: async (contactId: string): Promise<ApiResponse<BvContact & { client_name?: string }>> => {
    try {
      const { data, error } = await supabase
        .from('contact')
        .select('id, org_id, client_id, first_name, last_name, email, phone, role, created_at, client(name)')
        .eq('id', contactId)
        .single();
      if (error) throw error;
      const row = data as Record<string, unknown>;
      const clientObj = row.client as { name?: string } | null;
      return { success: true, data: { ...row, client_name: clientObj?.name ?? undefined } as BvContact & { client_name?: string } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  listAllContacts: async (): Promise<ApiResponse<(BvContact & { client_name?: string })[]>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const PAGE = 1000;
      const allContacts: (BvContact & { client_name?: string })[] = [];
      let page = 0;
      while (true) {
        const { data, error } = await supabase
          .from('contact')
          .select('id, org_id, client_id, first_name, last_name, email, phone, role, bv_extn, mobile, contact_type, is_primary, site_id, created_at, client(name, sf_account_id), site(nmi, sf_site_id)')
          .eq('org_id', orgId)
          .order('first_name')
          .order('id')
          .range(page * PAGE, (page + 1) * PAGE - 1);
        if (error) throw error;
        const mapped = (data ?? []).map((c: unknown) => {
          const row = c as Record<string, unknown>;
          const clientObj = row.client as { name?: string; sf_account_id?: string } | null;
          const siteObj = row.site as { nmi?: string; sf_site_id?: string } | null;
          return {
            ...row,
            client_name: clientObj?.name ?? undefined,
            client_sf_account_id: clientObj?.sf_account_id ?? undefined,
            site_nmi: siteObj?.nmi ?? undefined,
            site_sf_site_id: siteObj?.sf_site_id ?? undefined,
          } as BvContact & { client_name?: string; client_sf_account_id?: string; site_nmi?: string; site_sf_site_id?: string };
        });
        allContacts.push(...mapped);
        if (!data?.length || data.length < PAGE) break;
        page++;
      }
      return { success: true, data: allContacts };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  listContacts: async (clientId: string): Promise<ApiResponse<BvContact[]>> => {
    try {
      const { data, error } = await supabase
        .from('contact')
        .select('id, org_id, client_id, first_name, last_name, email, phone, role, bv_extn, mobile, contact_type, is_primary, site_id, created_at, site(nmi, sf_site_id)')
        .eq('client_id', clientId)
        .order('created_at');
      if (error) throw error;
      return { success: true, data: data ?? [] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  createContact: async (clientId: string, form: BvContactFormData): Promise<ApiResponse<BvContact>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const { mobile, contact_type, is_primary, ...base } = form;
      const bv_extn = { mobile, contact_type, is_primary };

      const { data, error } = await supabase
        .from('contact')
        .insert({ ...base, client_id: clientId, org_id: orgId, bv_extn })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  updateContact: async (contactId: string, form: Partial<BvContactFormData>): Promise<ApiResponse<BvContact>> => {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('contact')
        .select('bv_extn')
        .eq('id', contactId)
        .single();
      if (fetchErr) throw fetchErr;

      const { mobile, contact_type, is_primary, ...base } = form;
      const bv_extn = {
        ...(existing?.bv_extn ?? {}),
        ...(mobile !== undefined ? { mobile } : {}),
        ...(contact_type !== undefined ? { contact_type } : {}),
        ...(is_primary !== undefined ? { is_primary } : {}),
      };

      const { data, error } = await supabase
        .from('contact')
        .update({ ...base, bv_extn })
        .eq('id', contactId)
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  deleteContact: async (contactId: string): Promise<ApiResponse<null>> => {
    try {
      const { error } = await supabase.from('contact').delete().eq('id', contactId);
      if (error) throw error;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
